// ═══════════════════════════════════════════════════════════════
// Calendar.jsx — verticalstack XC-CAL Calendar
// Path: src/components/Calendar.jsx
//
// S10.B (Jul 8, 2026) — NEW FILE. Ports sandbox v7.2 xcCalendar()
// function from the vanilla-JS HTML idiom to React JSX. Sixth unlocked
// Cross-cutting nav item (ShellNav xc-calendar flip). Structural sibling
// of Documents.jsx (S5.B): same db-from-../firebase/config import, same
// workspaceId query + soft-fallback-retry pattern, same banner / KPI /
// table layout. Adds a live write path (+ New event) — the FIRST write
// onto /events (rules block + §6b schema live since S5; zero docs until
// this session).
//
// DESIGN INTENT (Strategy §3b, Taxonomy PLATFORM-COMPONENTS row 5)
//   The one surface showing every dated obligation across the whole
//   portfolio — closings, inspections, PM tasks, WO due dates, lease
//   renewals, 1031 deadlines, permit milestones, draw schedules, K-1
//   distributions — in a single universal calendar with the responsible
//   party named per event. Dated-event backbone: every event here is
//   what XC-TRIG (S11) later watches and fires on (P0 build-first).
//
// SANDBOX FIDELITY (Demo v7.2 xcCalendar)
//   Renders a stage-filtered "Upcoming events" TABLE (Date · Type ·
//   Parcel · Responsible Party · Days out · Action) above four KPI cards
//   (Upcoming · Next 30 days · Unassigned · iCal subscribers stub), with
//   page actions Export iCal / Today view / + New event and an iCal band.
//   NOT a month grid (that was the deprecated M-02 view). Cross-cutting
//   nav has no stage context → stage-agnostic; shows all workspace events
//   (AS-S10-CAL-STAGE-AGNOSTIC, same posture as Network/Marketplace).
//
// CANONICAL SCHEMA (S5 close handoff §6b — /events, 12 fields)
//   Required: id, workspaceId, type, title, startAt, createdBy, createdAt
//   Optional: parcelId, endAt, attendees[], notes, updatedAt
//   S10 AMENDMENT (AS-S10-RESPONSIBLE-PARTY): optional `responsibleParty`
//     (string) added so the sandbox Responsible Party column + ICS
//     ATTENDEE line have a home. Additive — no rules change, no migration
//     (zero docs). Phase-1 free text; Phase-2 resolves to an XC-TEAM id.
//   NOTE (AS-S10-TYPE-FIELD): the field is `type`, not `eventType`
//     (Schedule-Taxonomy "eventType" was loose narrative).
//
// iCal EXPORT (S10.C, read-only Phase 1)
//   Export iCal opens the calendarFeed Cloud Function feed for this
//   workspace. Feed URL base is CALENDAR_FEED_BASE below — CONFIRM /
//   replace with the deployed URL printed by `firebase functions:list`
//   after S10.C deploys (v2 onRequest functions may surface a *.run.app
//   URL; the cloudfunctions.net alias also resolves). Bidirectional sync
//   deferred Phase 2 (memo #8). Feed-token hardening pre-launch
//   (AS-S10-ICS-FEED-TOKEN) — workspaceId-in-URL is a guessable capability.
//
// SMOKE HARNESS
//   mockMode prop (boolean): when true, bypasses the Firestore read and
//   the write path, renders with a hardcoded 8-event mock array spanning
//   event types. Used by PlaceholderDashboard ?xccal-smoke=1 harness.
//
// FILTER WIRING (deferred per AS-S5-013)
//   XcFilterBar renders the filter chip UI (viewId "xc-calendar"); its
//   values are NOT consumed to filter the table in S10 (same posture as
//   Documents S5.B). If the bar renders empty/errors, xc-calendar is not
//   yet in xcFilterBar FILTER_CONFIGS — one-line add (AS-S10-FILTER-VIEWID).
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useState } from 'react';
import {
  collection, query, where, getDocs, addDoc, serverTimestamp, orderBy, Timestamp,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getApp } from 'firebase/app';
import { db } from '../firebase/config';
import XcFilterBar from './xcFilterBar';

// ── Firebase auth instance (derived from existing app) ───────────────────
const app = getApp();
const auth = getAuth(app);

// ── iCal feed base (S10.C calendarFeed CF) ───────────────────────────────
// CONFIRM after deploy — v2 onRequest URL from `firebase functions:list`.
const CALENDAR_FEED_BASE =
  'https://us-central1-vitality-services.cloudfunctions.net/calendarFeed';

// ── Best-effort icon by event type (display only; type is free text) ─────
function iconForType(type) {
  const t = (type || '').toLowerCase();
  if (t.includes('clos'))                          return '🤝';
  if (t.includes('inspect'))                       return '🔍';
  if (t.includes('permit'))                        return '📋';
  if (t.includes('draw'))                          return '💰';
  if (t.includes('lease') || t.includes('renew'))  return '📄';
  if (t.includes('nnn') || t.includes('expiry'))   return '⚠';
  if (t.includes('pm') || t.includes('mainten'))   return '🔧';
  if (t.includes('wo') || t.includes('work order'))return '🛠';
  if (t.includes('warrant'))                       return '⏳';
  if (t.includes('1031'))                          return '⏱';
  if (t.includes('k-1') || t.includes('distrib'))  return '📋';
  if (t.includes('tax'))                           return '🏛';
  if (t.includes('valu'))                          return '📊';
  if (t.includes('cruise') || t.includes('timber'))return '🌲';
  if (t.includes('sale') || t.includes('exit'))    return '⚖';
  if (t.includes('rent'))                          return '💵';
  if (t.includes('meeting'))                       return '📅';
  return '•';
}

// ── Mock data for smoke harness (8 events spanning types) ────────────────
const MOCK_EVENTS = [
  { id: 'mock-1', type: 'Inspection',      title: 'Roof inspection',          parcelLabel: 'A-1 Unit 7',          responsibleParty: 'EverGuard',      startAt: '2026-07-14' },
  { id: 'mock-2', type: 'PM Task',         title: 'HVAC filter change',       parcelLabel: 'A-1 Canton',          responsibleParty: 'EverGuard',      startAt: '2026-07-18' },
  { id: 'mock-3', type: 'Lease Renewal',   title: 'Unit 4 renewal window',    parcelLabel: 'A-1 Unit 4',          responsibleParty: 'PM Larry',       startAt: '2026-09-15' },
  { id: 'mock-4', type: 'Draw Schedule',   title: 'Draw #4 submission',       parcelLabel: 'Liberty Commons',     responsibleParty: 'ERIEBANK',       startAt: '2026-08-01' },
  { id: 'mock-5', type: 'Permit Milestone',title: 'Building dept sign-off',   parcelLabel: 'Liberty Commons',     responsibleParty: 'Building Dept',  startAt: '2026-07-22' },
  { id: 'mock-6', type: 'NNN Lease Expiry',title: 'Potters NNN expiry',       parcelLabel: 'Potters Industries',  responsibleParty: '',               startAt: '2026-12-02' },
  { id: 'mock-7', type: 'K-1 Distribution',title: 'Red Oak K-1 delivery',     parcelLabel: 'Red Oak Partners',    responsibleParty: 'CPA',            startAt: '2027-03-15' },
  { id: 'mock-8', type: 'Warranty Expiry', title: 'RTU warranty ends',        parcelLabel: 'A-1 RTU',             responsibleParty: '',               startAt: '2026-08-15' },
];

// ── Date helpers ─────────────────────────────────────────────────────────
function toDate(v) {
  if (!v) return null;
  if (typeof v === 'string') return new Date(v + 'T00:00:00');
  if (v.toDate) return v.toDate();
  if (v instanceof Date) return v;
  return null;
}
function dateKey(d) {
  return d ? d.toISOString().slice(0, 10) : '—';
}
function daysOut(d) {
  if (!d) return null;
  const ms = d.getTime() - Date.now();
  return Math.ceil(ms / 86400000);
}

// ── Inline styles (mirror Documents.jsx conventions) ─────────────────────
const styles = {
  container: { maxWidth: 1100, margin: '0 auto', fontFamily: 'Arial, sans-serif' },
  banner: {
    backgroundColor: '#E6F4F8', border: '1px solid rgba(0, 184, 208, 0.25)',
    borderRadius: 8, padding: '12px 18px', marginBottom: 20, display: 'flex',
    justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12,
  },
  bannerTitle: { fontSize: 13, fontWeight: 700, color: '#003060' },
  bannerTag: {
    display: 'inline-block', marginLeft: 8, padding: '2px 8px', fontSize: 10,
    fontWeight: 700, color: '#006B7D', background: 'rgba(0, 184, 208, 0.15)',
    borderRadius: 10, textTransform: 'uppercase', letterSpacing: 0.4,
    fontFamily: 'Consolas, monospace',
  },
  bannerMuted: { fontSize: 12, color: '#6B7280' },
  pageHead: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
    marginBottom: 18, flexWrap: 'wrap', gap: 12,
  },
  pageTitle: { fontSize: 24, fontWeight: 700, color: '#003060', margin: 0, marginBottom: 4 },
  pageSub: { fontSize: 13, color: '#6B7280', margin: 0 },
  pageActions: { display: 'flex', gap: 8 },
  btnGhost: {
    padding: '8px 14px', backgroundColor: '#FFFFFF', border: '1px solid #D1D5DB',
    borderRadius: 6, fontSize: 13, fontWeight: 600, color: '#374151',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  btnGhostDisabled: {
    padding: '8px 14px', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB',
    borderRadius: 6, fontSize: 13, fontWeight: 600, color: '#9CA3AF',
    cursor: 'not-allowed', fontFamily: 'inherit',
  },
  btnPrimary: {
    padding: '8px 14px', backgroundColor: '#003060', border: '1px solid #003060',
    borderRadius: 6, fontSize: 13, fontWeight: 600, color: '#FFFFFF',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  icalBand: {
    background: '#F0F9FB', border: '1px solid rgba(0, 184, 208, 0.25)',
    borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13,
    color: '#374151', lineHeight: 1.55,
  },
  icalCode: {
    background: '#FFF', padding: '1px 6px', borderRadius: 3,
    fontFamily: 'Consolas, monospace', fontSize: 11, color: '#003060',
    wordBreak: 'break-all',
  },
  kpiGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 12, marginBottom: 20,
  },
  kpiCard: { backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 8, padding: 14 },
  kpiCardAccent: {
    backgroundColor: '#FFFFFF', border: '1px solid rgba(0, 184, 208, 0.3)',
    borderLeft: '3px solid #00B8D0', borderRadius: 8, padding: 14,
  },
  kpiCardWarn: {
    backgroundColor: '#FFFFFF', border: '1px solid rgba(217, 119, 6, 0.3)',
    borderLeft: '3px solid #D97706', borderRadius: 8, padding: 14,
  },
  kpiLabel: {
    fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase',
    letterSpacing: 0.4, marginBottom: 6,
  },
  kpiVal: { fontSize: 26, fontWeight: 700, color: '#003060', lineHeight: 1.1 },
  kpiSub: { fontSize: 11, color: '#9CA3AF', marginTop: 4 },
  card: { backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden' },
  cardHead: { padding: '14px 18px', borderBottom: '1px solid #F3F4F6', backgroundColor: '#FAFBFC' },
  cardTitle: { fontSize: 14, fontWeight: 700, color: '#003060', margin: 0 },
  cardSub: { fontSize: 12, color: '#6B7280', margin: 0, marginTop: 2 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase',
    letterSpacing: 0.5, textAlign: 'left', padding: '10px 14px',
    borderBottom: '1px solid #E5E7EB', backgroundColor: '#F9FAFB',
  },
  thNum: {
    fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase',
    letterSpacing: 0.5, textAlign: 'right', padding: '10px 14px',
    borderBottom: '1px solid #E5E7EB', backgroundColor: '#F9FAFB',
  },
  td: { padding: '12px 14px', borderBottom: '1px solid #F3F4F6', fontSize: 13, color: '#374151' },
  tdNum: { padding: '12px 14px', borderBottom: '1px solid #F3F4F6', fontSize: 13, color: '#374151', textAlign: 'right' },
  emptyRow: { textAlign: 'center', padding: 20, color: '#9CA3AF', fontStyle: 'italic' },
  mono: { fontFamily: 'Consolas, monospace', fontSize: 11 },
  unassigned: { color: '#B45309', fontStyle: 'italic', fontSize: 12 },
  // New-event inline form
  formCard: {
    backgroundColor: '#FFFFFF', border: '1px solid rgba(0, 184, 208, 0.3)',
    borderRadius: 8, padding: 16, marginBottom: 20,
  },
  formRow: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' },
  formField: { display: 'flex', flexDirection: 'column', gap: 4 },
  formLabel: { fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.4 },
  input: {
    padding: '7px 9px', border: '1px solid #D1D5DB', borderRadius: 6,
    fontSize: 13, color: '#374151', outline: 'none', fontFamily: 'inherit',
  },
  formStatus: { fontSize: 12, color: '#6B7280', marginTop: 8 },
  formError: { fontSize: 12, color: '#B91C1C', marginTop: 8 },
};

// ── Main component ───────────────────────────────────────────────────────

export default function Calendar({ workspace, roles, roleLabel, mockMode = false }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [formDate, setFormDate] = useState('');
  const [formParty, setFormParty] = useState('');
  const [saving, setSaving] = useState(false);
  const [formStatus, setFormStatus] = useState(null);
  const [formError, setFormError] = useState(null);

  const workspaceId = workspace?.workspaceId;

  // ── Load /events scoped to workspace ────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    async function loadEvents() {
      if (mockMode) { setEvents(MOCK_EVENTS); setLoading(false); return; }
      if (!workspaceId) { setEvents([]); setLoading(false); return; }

      setLoading(true);
      setError(null);
      try {
        const q = query(
          collection(db, 'events'),
          where('workspaceId', '==', workspaceId),
          orderBy('startAt', 'asc')
        );
        const snap = await getDocs(q);
        if (cancelled) return;
        setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        if (cancelled) return;
        // Retry without orderBy (mirrors Documents / PlaceholderDashboard pattern).
        // Catches the missing composite-index error as a soft fallback so smoke
        // passes before the workspaceId+startAt index finishes building.
        // eslint-disable-next-line no-console
        console.warn('[Calendar] Query failed, retrying without orderBy:', err.message);
        try {
          const q2 = query(collection(db, 'events'), where('workspaceId', '==', workspaceId));
          const snap2 = await getDocs(q2);
          if (cancelled) return;
          setEvents(snap2.docs.map((d) => ({ id: d.id, ...d.data() })));
        } catch (err2) {
          if (cancelled) return;
          setError(err2.message || 'Failed to load events');
          setEvents([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadEvents();
    return () => { cancelled = true; };
  }, [workspaceId, mockMode]);

  // ── + New event → first write onto /events (§6b shape) ──────────────────
  async function handleCreateEvent() {
    setFormError(null);
    if (mockMode) { setFormStatus('Mock mode — create disabled'); return; }
    if (!workspaceId) { setFormError('No workspace selected'); return; }
    if (!formTitle.trim() || !formDate) { setFormError('Title and date are required'); return; }

    // createdBy must equal request.auth.uid per /events create rule.
    const uid = auth.currentUser?.uid;
    if (!uid) { setFormError('No authenticated user — sign in required'); return; }

    setSaving(true);
    setFormStatus('Writing event…');
    try {
      const startDate = new Date(formDate + 'T00:00:00');
      const record = {
        workspaceId,
        type: formType.trim() || 'Event',
        title: formTitle.trim(),
        startAt: Timestamp.fromDate(startDate),
        createdBy: uid,
        createdAt: serverTimestamp(),
        // Optional §6b fields
        responsibleParty: formParty.trim() || null,  // AS-S10-RESPONSIBLE-PARTY
        parcelId: null,
        endAt: null,
      };
      const ref = await addDoc(collection(db, 'events'), record);
      setFormStatus(`Event created (${ref.id}).`);

      // Refresh (unordered — avoids index dependency on the refresh path)
      const snap = await getDocs(
        query(collection(db, 'events'), where('workspaceId', '==', workspaceId))
      );
      setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));

      // Reset form
      setFormType(''); setFormTitle(''); setFormDate(''); setFormParty('');
      setShowForm(false);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Calendar] Create event failed:', err);
      setFormError(err.message || 'Create failed');
      setFormStatus(null);
    } finally {
      setSaving(false);
    }
  }

  function handleExportIcal() {
    if (!workspaceId) return;
    const url = `${CALENDAR_FEED_BASE}?ws=${encodeURIComponent(workspaceId)}`;
    window.open(url, '_blank');
  }

  // ── Derived view: upcoming, sorted, with days-out ───────────────────────
  const nowStart = new Date(); nowStart.setHours(0, 0, 0, 0);
  const decorated = events
    .map((e) => {
      const d = toDate(e.startAt);
      return { ...e, _date: d, _days: daysOut(d) };
    })
    .sort((a, b) => (a._date?.getTime() || 0) - (b._date?.getTime() || 0));

  const upcoming = decorated.filter((e) => e._date && e._date >= nowStart);
  const next30 = upcoming.filter((e) => e._days != null && e._days <= 30);
  const unassigned = decorated.filter((e) => !e.responsibleParty).length;

  const feedUrl = workspaceId
    ? `${CALENDAR_FEED_BASE}?ws=${workspaceId}`
    : `${CALENDAR_FEED_BASE}?ws={workspaceId}`;

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div style={styles.container}>
      {/* Banner */}
      <div style={styles.banner}>
        <div>
          <span style={styles.bannerTitle}>Calendar</span>
          <span style={styles.bannerTag}>XC-CAL</span>
        </div>
        <div style={styles.bannerMuted}>
          Universal events · responsible party tracked · iCal export READ-ONLY (Phase 1)
        </div>
      </div>

      {/* Page head */}
      <div style={styles.pageHead}>
        <div>
          <h1 style={styles.pageTitle}>Calendar</h1>
          <p style={styles.pageSub}>
            {upcoming.length} upcoming · {next30.length} in next 30 days · universal calendar (all stages)
          </p>
        </div>
        <div style={styles.pageActions}>
          <button
            type="button"
            style={mockMode ? styles.btnGhostDisabled : styles.btnGhost}
            onClick={handleExportIcal}
            disabled={mockMode}
          >
            📅 Export iCal (.ics)
          </button>
          <button type="button" style={styles.btnGhostDisabled} disabled>
            ⎕ Today view
          </button>
          <button
            type="button"
            style={mockMode ? styles.btnGhostDisabled : styles.btnPrimary}
            onClick={() => setShowForm((v) => !v)}
            disabled={mockMode}
          >
            + New event
          </button>
        </div>
      </div>

      {/* iCal read-only band */}
      <div style={styles.icalBand}>
        <b>iCal export (Phase 1 read-only):</b> subscribe to{' '}
        <code style={styles.icalCode}>{feedUrl}</code>{' '}
        from Google Cal / Apple Cal / Outlook. Bidirectional sync (events created in
        an external calendar write back) is deferred to Phase 2 (memo #8).
      </div>

      {/* New-event inline form */}
      {showForm && !mockMode && (
        <div style={styles.formCard}>
          <div style={styles.formRow}>
            <div style={styles.formField}>
              <label style={styles.formLabel}>Type</label>
              <input
                style={styles.input}
                value={formType}
                onChange={(e) => setFormType(e.target.value)}
                placeholder="e.g. Inspection"
              />
            </div>
            <div style={{ ...styles.formField, flex: 1, minWidth: 180 }}>
              <label style={styles.formLabel}>Title *</label>
              <input
                style={styles.input}
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="Event title"
              />
            </div>
            <div style={styles.formField}>
              <label style={styles.formLabel}>Date *</label>
              <input
                style={styles.input}
                type="date"
                value={formDate}
                onChange={(e) => setFormDate(e.target.value)}
              />
            </div>
            <div style={styles.formField}>
              <label style={styles.formLabel}>Responsible party</label>
              <input
                style={styles.input}
                value={formParty}
                onChange={(e) => setFormParty(e.target.value)}
                placeholder="e.g. EverGuard"
              />
            </div>
            <button
              type="button"
              style={saving ? styles.btnGhostDisabled : styles.btnPrimary}
              onClick={handleCreateEvent}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save event'}
            </button>
          </div>
          {formStatus && <div style={styles.formStatus}>{formStatus}</div>}
          {formError && <div style={styles.formError}>{formError}</div>}
        </div>
      )}

      {/* XC-FILTER (renders chip UI; values not consumed in S10 per AS-S5-013) */}
      <XcFilterBar viewId="xc-calendar" />

      {/* KPI grid */}
      <div style={styles.kpiGrid}>
        <div style={styles.kpiCardAccent}>
          <div style={styles.kpiLabel}>Upcoming</div>
          <div style={styles.kpiVal}>{upcoming.length}</div>
          <div style={styles.kpiSub}>workspace events</div>
        </div>
        <div style={next30.length > 2 ? styles.kpiCardWarn : styles.kpiCard}>
          <div style={styles.kpiLabel}>Next 30 days</div>
          <div style={styles.kpiVal}>{next30.length}</div>
          <div style={styles.kpiSub}>attention window</div>
        </div>
        <div style={styles.kpiCard}>
          <div style={styles.kpiLabel}>Unassigned</div>
          <div style={styles.kpiVal}>{unassigned}</div>
          <div style={styles.kpiSub}>no responsible party</div>
        </div>
        <div style={styles.kpiCard}>
          <div style={styles.kpiLabel}>iCal subscribers</div>
          <div style={styles.kpiVal}>—</div>
          <div style={styles.kpiSub}>Phase 1 stub</div>
        </div>
      </div>

      {/* Events table */}
      <div style={styles.card}>
        <div style={styles.cardHead}>
          <div style={styles.cardTitle}>Upcoming events</div>
          <div style={styles.cardSub}>Universal calendar · same UI all stages · responsible party per event</div>
        </div>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Date</th>
              <th style={styles.th}>Type</th>
              <th style={styles.th}>Parcel</th>
              <th style={styles.th}>Responsible Party</th>
              <th style={styles.thNum}>Days out</th>
              <th style={styles.th}>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} style={styles.emptyRow}>Loading events…</td></tr>
            )}
            {!loading && error && (
              <tr><td colSpan={6} style={{ ...styles.emptyRow, color: '#B91C1C' }}>
                Could not load events: {error}
              </td></tr>
            )}
            {!loading && !error && decorated.length === 0 && (
              <tr><td colSpan={6} style={styles.emptyRow}>
                No events yet. Click “+ New event” to add the first.
              </td></tr>
            )}
            {!loading && !error && decorated.map((e) => {
              const parcelLabel = e.parcelLabel || e.parcelId || '—';
              const party = e.responsibleParty;
              return (
                <tr key={e.id}>
                  <td style={styles.td}><span style={styles.mono}>{dateKey(e._date)}</span></td>
                  <td style={styles.td}>
                    <span style={{ marginRight: 6 }}>{iconForType(e.type)}</span>
                    <span style={{ fontWeight: 600, fontSize: 12 }}>{e.type || '(untyped)'}</span>
                    {e.title && (
                      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{e.title}</div>
                    )}
                  </td>
                  <td style={styles.td}>
                    <div style={{ fontSize: 12, color: '#6B7280' }}>{parcelLabel}</div>
                  </td>
                  <td style={styles.td}>
                    {party
                      ? <span style={{ fontSize: 12 }}>{party}</span>
                      : <span style={styles.unassigned}>Unassigned</span>}
                  </td>
                  <td style={styles.tdNum}>
                    {e._days == null ? '—'
                      : e._days < 0 ? <span style={{ color: '#B91C1C' }}>{e._days}d</span>
                      : `${e._days}d`}
                  </td>
                  <td style={styles.td}>
                    <button type="button" style={styles.btnGhost} disabled>
                      View
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
